import React, {useCallback} from 'react';

import {CaptionedItemProps} from 'notebook/mediocre/base-plugin/plugins';
import {EditorProps, MediocreItemEditor} from 'notebook/mediocre/EditorTypes';
import {TableItemProps} from 'notebook/mediocre/MediocreConstants';
import APIUtil from 'util/APIUtil';
import {isIndexTreeEmpty} from 'util/IndexTree';

import {forwardSearchResultProps, MediocreSearchResultProps} from '../search/SearchResultProps';
import Revirtualizable from './Revirtualizable';
import {isRevirtualizationEnabled} from './Revirtualization';
import Virtualizable from './Virtualizable';
import {useVirtualizeItem} from './Virtualization';

const VIRTUALIZATION_DEFAULT_HEIGHT = 300;

function DefaultSkeleton({height}: {height: number | undefined}): JSX.Element {
  return <div style={{height}} />;
}

interface MediocreItemComponentProps<TItem> extends MediocreSearchResultProps {
  item: TItem;
  viewer?: boolean;
  editor: MediocreItemEditor<TItem>;
  editorProps: EditorProps<TItem>;
  index: number;
  itemProps: unknown;
}

/**
 * Renders a UI component for an item in a Mediocre editor... or its virtualized skeleton based on viewport.
 */
export default function MediocreItemComponent<TItem>(props: MediocreItemComponentProps<TItem>): JSX.Element {
  const type = props.editorProps.getType(props.item);
  const plugin = props.editor.getPluginByType(type);
  const cid = props.editorProps.getCid(props.item);
  const skipVirtualizationDueToPerformantRender =
    plugin.component.skipVirtualizationDueToPerformantRender?.(props.item) ?? false;
  const enableVirtualization =
    !props.viewer &&
    !skipVirtualizationDueToPerformantRender &&
    /* If selectedSearchResultTree is non-empty, then the search component will want to scroll the element
       into the viewport, which requires the element to be rendered to the screen, so we disable
       virtualization. */
    isIndexTreeEmpty(props.selectedSearchResultTree);

  const itemId = plugin.component.getVirtualizationId
    ? `${props.editorProps.fileId}_${type}_${plugin.component.getVirtualizationId?.(props.item)}`
    : props.editorProps.getCid(props.item);

  // For table items, they will have multiEditing set to true if they are currently being edited.
  // Currently edited tables should not virtualize since they will have a floating cell editor that scrolls
  // with the window.
  const itemIsBeingEdited =
    (props.itemProps as CaptionedItemProps<TableItemProps>)?.childItemProps?.multiEditing ?? false;

  const {ref, height, virtualizationState} = useVirtualizeItem<TItem>(
    props.item,
    itemId,
    enableVirtualization && !itemIsBeingEdited,
    () => {
      if (!plugin.component.getVirtualizedHeight) {
        return VIRTUALIZATION_DEFAULT_HEIGHT;
      }
      const isCollapsed =
        plugin.component.getIsCollapsed?.(
          props.item,
          props.editorProps,
          // If we are rendering a skeleton, then the item has not rendered yet and it wont have state in
          // redux, so we are okay with passing an empty object for `collapsedStateById`. While more robust to
          // use the actual redux state, we want to avoid prematurely introducing a dependency on redux as
          // that can result in a performance hit. If we decide to consolidate handling collapsed rendering in
          // this component more generally, we may want to revisit this.
          {}
        ) ?? false;
      return plugin.component.getVirtualizedHeight(props.item, isCollapsed);
    }
  );

  const renderVirtualizedSkeleton = useCallback(
    () =>
      plugin.component.renderVirtualizedSkeleton?.(props.item, height!) ?? (
        <DefaultSkeleton height={height} />
      ),
    [plugin.component, props.item, height]
  );

  const renderDevirtualizedItem = useCallback(
    () =>
      plugin.component.make.call(
        props.editor,
        props.item,
        props.index,
        props.editorProps,
        props.itemProps,
        forwardSearchResultProps(props)
      ),
    [plugin, props]
  );

  const Container = isRevirtualizationEnabled() ? Revirtualizable : Virtualizable;

  return (
    <div ref={ref}>
      <Container
        eventId={cid}
        contentType={type}
        documentType={APIUtil.apiIdToType(props.editorProps.fileId)}
        virtualizationState={virtualizationState}
        renderVirtualizedSkeleton={renderVirtualizedSkeleton}
        renderDevirtualizedItem={renderDevirtualizedItem}
      />
    </div>
  );
}
